<template>
  <b-form-group
    class="sid-range mb-2"
    :label="label"
  >
    <div class="flex items-center gap-[17px] justify-between">
      <vue-slider
        v-model="sliderValue"
        :interval="1"
        :max="sliderMax"
        :min="sliderMin"
        class="editor-slider-1 w-100"
        tooltip="none"
        @change="onChange"
      />

      <div :class="dimensions.length > 1 ? 'mt-[-26px]' : 'mt-[-10px]'">
        <div
          v-if="dimensions.length > 1"
          class="flex gap-[7px] text-[#A6A6A6] text-uppercase text-[11px] font-[600] mb-[11px]"
        >
          <span
            v-for="dim in dimensions"
            :key="dim"
            class="cursor-pointer hover:text-[#44474A]"
            :class="{ 'text-[#44474A]': currentUnit === dim }"
            @click="() => updateDimension(dim)"
            v-text="dim"
          />
        </div>

        <LinkedControl
          v-model="currentValue"
          :step="normalizedStep"
          :ignore-zero="ignoreZero"
          :max="getDimensionValue.max"
          :min="getDimensionValue.min"
          class="d-block !w-[57px] !text-[#7E7E7E] !text-[13px] !h-[29px]"
          :placeholder="resetValue === 'auto' ? 'auto' : '-'"
          :delay="300"
          @focusout.native="onChange"
          @keyup.enter.native="onChange"
          @keydown.native.stop
          @keyup.native.stop
        />
      </div>
    </div>

    <div class="flex justify-end pt-2">
      <span
        class="text-[13px] text-[#44474A] hover:opacity-80 cursor-pointer underline"
        @click="reset"
      >
        Reset
      </span>
    </div>
  </b-form-group>
</template>

<script>
import LinkedControl from '@/components/editor/components/linked-control-group/LinkedControl'

export default {
  name: 'RangeSlider',
  components: { LinkedControl },
  model: {
    prop: 'value',
    event: 'input'
  },
  props: {
    /* external model */
    value: {
      type: [Number, String],
      default: ''
    },
    unit: {
      type: String,
      default: 'px'
    },

    /* UI config */
    resetValue: {
      type: [Number, String],
      default: ''
    },
    defaultDimension: {
      type: String,
      default: 'px'
    },
    max: {
      type: [Number, Object],
      default: 100
    },
    min: {
      type: [Number, Object],
      default: 0
    },
    step: {
      type: [Number, Object],
      default: 1
    },
    dimensions: {
      type: Array,
      default: () => ['px', 'rem', '%']
    },
    label: {
      type: String,
      default: ''
    },
    ignoreZero: {
      type: Boolean,
      default: false
    }
  },
  // emits removed for Vue 2 compatibility with v-model
  computed: {
    normalizedStep () {
      const unit = this.currentUnit

      let rawStep

      // step is a single number -> same for all units
      if (typeof this.step === 'number') {
        rawStep = this.step
      } else if (this.step && typeof this.step === 'object' && unit in this.step) {
        // step is per-unit object
        const v = this.step[unit]
        rawStep = typeof v === 'number' ? v : 1
      } else {
        // fallback
        rawStep = 1
      }

      const n = Number(rawStep)
      // Fallback to 1 if step is invalid or non-positive
      return isNaN(n) || n <= 0 ? 1 : n
    },
    sliderFactor () {
      const step = this.normalizedStep
      // avoid division by zero; fallback factor 1
      return step > 0 ? 1 / step : 1
    },
    sliderMin () {
      return this.getDimensionValue.min * this.sliderFactor
    },
    sliderMax () {
      return this.getDimensionValue.max * this.sliderFactor
    },
    currentUnit: {
      get () {
        if (!this.dimensions.length) return ''
        return this.unit || this.defaultDimension
      },
      set (val) {
        this.$emit('update:unit', val)

        const { min, max } = this.getDimensionValue
        if (this.currentValue !== '') {
          if (this.currentValue > max) this.currentValue = max
          else if (this.currentValue < min) this.currentValue = min
        }

        this.emitChange()
      }
    },
    currentValue: {
      get () {
        const n = parseFloat(this.value)
        return isNaN(n) ? '' : n
      },
      set (val) {
        const num = Number(val)

        if (isNaN(num)) {
          this.$emit('input', '')
        } else {
          const { min, max } = this.getDimensionValue
          let clamped = num
          if (clamped < min) clamped = min
          if (clamped > max) clamped = max
          this.$emit('input', clamped)
        }
      }
    },
    sliderValue: {
      get () {
        const { min, max } = this.getDimensionValue
        const n = parseFloat(this.value)

        // If value is not a number yet, default slider position to min
        let base = isNaN(n) ? min : n

        let clamped = base
        if (clamped < min) clamped = min
        if (clamped > max) clamped = max

        return clamped * this.sliderFactor
      },
      set (val) {
        const n = Number(val)
        if (isNaN(n)) {
          this.currentValue = ''
        } else {
          const raw = n / this.sliderFactor
          const { min, max } = this.getDimensionValue
          let clamped = raw
          if (clamped < min) clamped = min
          if (clamped > max) clamped = max
          this.currentValue = clamped
        }
      }
    },
    getDimensionValue () {
      const unit = this.currentUnit

      const resolveLimit = (limitProp, fallback) => {
        if (typeof limitProp === 'number') return limitProp

        if (limitProp && typeof limitProp === 'object' && unit in limitProp) {
          const v = limitProp[unit]
          return typeof v === 'number' ? v : fallback
        }

        return fallback
      }

      const defaultMax = typeof this.max === 'number' ? this.max : 100
      const defaultMin = typeof this.min === 'number' ? this.min : 0

      return {
        max: resolveLimit(this.max, defaultMax),
        min: resolveLimit(this.min, defaultMin)
      }
    }
  },
  watch: {
    getDimensionValue: {
      handler ({ min, max }) {
        if (this.currentValue === '' || this.currentValue == null) return

        const n = Number(this.currentValue)
        if (isNaN(n)) return

        let clamped = n
        if (n < min) clamped = min
        if (n > max) clamped = max

        if (clamped !== n) {
          this.currentValue = clamped
          this.$emit('input', clamped)
        }
      },
      deep: true,
      immediate: true
    }
  },
  mounted () {
    if (this.currentValue > this.getDimensionValue.max) {
      this.currentValue = this.getDimensionValue.max
    } else if (this.currentValue < this.getDimensionValue.min) {
      this.currentValue = this.getDimensionValue.min
    }
  },
  methods: {
    onChange () {
      this.$emit('change', {
        value: this.currentValue,
        unit: this.currentUnit
      })
    },
    reset () {
      this.$emit('reset')
    },
    emitChange () {
      this.$emit('change', {
        value: this.currentValue,
        unit: this.currentUnit
      })
    },
    updateDimension (val) {
      this.currentUnit = val
    }
  }
}
</script>
